import React, { useState } from 'react';
import { Upload, CheckCircle, AlertCircle, Loader2, Package, Image, Euro, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/sonner';
import { BACKEND_URL } from '../../config/api';

const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'basic', label: 'Alarme Baseline' },
  { value: 'video_basic', label: 'Vidéosurveillance Baseline' },
  { value: 'pro', label: 'Alarme Pro (Sur devis)' },
  { value: 'video_pro', label: 'Vidéo Pro (Sur devis)' },
  { value: 'fire', label: 'Incendie' },
  { value: 'automation', label: 'Domotique' },
];

const SUBCATEGORIES = {
  basic: ['hubs', 'kits', 'detectors', 'sirens', 'keypads', 'remotes', 'relays'],
  video_basic: ['cameras', 'nvr', 'storage', 'accessories'],
  pro: ['centrals', 'detectors', 'keypads'],
  video_pro: ['cameras', 'nvr'],
  fire: ['detectors', 'sirens'],
  automation: ['switches', 'sockets', 'relays'],
};

const VisiotechImportPage = () => {
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('video_basic');
  const [subcategory, setSubcategory] = useState('cameras');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [imported, setImported] = useState(null);
  const [error, setError] = useState(null);

  const handlePreview = async () => {
    if (!url.includes('visiotechsecurity.com')) {
      toast.error('URL invalide', { description: 'L\'URL doit être de visiotechsecurity.com' });
      return;
    }

    setLoading(true);
    setError(null);
    setPreview(null);
    setImported(null);

    try {
      const response = await fetch(`${API}/admin/import/visiotech/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, category, subcategory })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la prévisualisation');
      }

      setPreview(data);
      toast.success('Produit trouvé !');
    } catch (err) {
      setError(err.message);
      toast.error('Erreur', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API}/admin/import/visiotech`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, category, subcategory })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'import');
      }

      setImported(data);
      setPreview(null);
      toast.success('Produit importé !', { description: `ID: ${data.product_id}` });
    } catch (err) {
      setError(err.message);
      toast.error('Erreur', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-fgs-bg py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Import Visiotech
          </h1>
          <p className="text-white/60">
            Importez automatiquement des produits depuis le catalogue Visiotech
          </p>
        </div>

        {/* Import Form */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                URL du produit Visiotech
              </label>
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.visiotechsecurity.com/fr/produits/..."
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Catégorie
                </label>
                <select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setSubcategory(SUBCATEGORIES[e.target.value]?.[0] || '');
                  }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Sous-catégorie
                </label>
                <select
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                >
                  {(SUBCATEGORIES[category] || []).map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              onClick={handlePreview}
              disabled={loading || !url}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Prévisualiser le produit
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Preview */}
        {preview && preview.product && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-400" />
              Prévisualisation
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Images */}
              <div>
                <div className="aspect-square bg-zinc-800 rounded-lg overflow-hidden mb-2">
                  {preview.product.image_url && (
                    <img
                      src={preview.product.image_url}
                      alt={preview.product.name}
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {Object.values(preview.product.images || {}).slice(0, 4).map((img, i) => (
                    <div key={i} className="w-16 h-16 bg-zinc-800 rounded overflow-hidden">
                      <img src={img} alt="" className="w-full h-full object-contain" />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/50 mt-2 flex items-center gap-1">
                  <Image className="w-3 h-3" />
                  {Object.keys(preview.product.images || {}).length} images importées
                </p>
              </div>

              {/* Info */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {preview.product.name}
                  </h3>
                  <p className="text-sm text-white/60 mt-1">
                    {preview.product.description}
                  </p>
                </div>

                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-white/60">Prix PVP HT (Visiotech)</span>
                    <span className="text-white font-medium">
                      {preview.scraped_data?.price_ht?.toFixed(2)} €
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">TVA (20%)</span>
                    <span className="text-white font-medium">
                      +{(preview.scraped_data?.price_ht * 0.20).toFixed(2)} €
                    </span>
                  </div>
                  <div className="border-t border-zinc-700 pt-2 flex justify-between">
                    <span className="text-emerald-400 font-semibold">Prix TTC final</span>
                    <span className="text-emerald-400 font-bold text-lg flex items-center gap-1">
                      <Euro className="w-4 h-4" />
                      {preview.product.price?.toFixed(2)} €
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-white/50">
                  <span className="px-2 py-1 bg-zinc-800 rounded">{category}</span>
                  <span className="px-2 py-1 bg-zinc-800 rounded">{subcategory}</span>
                </div>

                <Button
                  onClick={handleImport}
                  disabled={loading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Importer ce produit
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Success */}
        {imported && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-emerald-400">
                  Produit importé avec succès !
                </h3>
                <p className="text-sm text-white/60">
                  ID: {imported.product_id}
                </p>
              </div>
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-white/60">Nom</span>
                <span className="text-white">{imported.product_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Prix HT</span>
                <span className="text-white">{imported.price_ht?.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Prix TTC</span>
                <span className="text-emerald-400 font-bold">{imported.price_ttc?.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Images</span>
                <span className="text-white">{imported.images_count} images</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setUrl('');
                  setPreview(null);
                  setImported(null);
                }}
                variant="outline"
                className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
              >
                Importer un autre produit
              </Button>
              <a
                href={`/product/${imported.product_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-zinc-700 hover:bg-zinc-600">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Voir le produit
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 p-4 bg-zinc-900/30 border border-zinc-800/50 rounded-lg">
          <h4 className="text-sm font-medium text-white/80 mb-2">Comment ça marche :</h4>
          <ol className="text-sm text-white/60 space-y-1 list-decimal list-inside">
            <li>Copiez l'URL d'un produit depuis visiotechsecurity.com</li>
            <li>Sélectionnez la catégorie et sous-catégorie appropriées</li>
            <li>Cliquez sur "Prévisualiser" pour vérifier les données</li>
            <li>Le prix TTC est calculé automatiquement (PVP HT + 20% TVA)</li>
            <li>Cliquez sur "Importer" pour ajouter le produit au catalogue</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default VisiotechImportPage;
